import { GlobeIcon, MagnifyingGlassIcon, ViewHorizontalIcon, DesktopIcon} from '@radix-ui/react-icons';
import { Box, Flex, Heading, TextField } from '@radix-ui/themes';
import { CountryState, PlatformState, SearchState, LayoutState, AudienceViewMode } from '../../types';
import { useEffect } from 'react';
import FilterToggleGroup, { FilterOption } from '../FilterToggleGroup';
import GenericSelect from '../GenericSelect';

type Props = {
  countryState?: CountryState;
  platformState?: PlatformState;
  searchState?: SearchState;
  layoutState?: LayoutState;
  visible: boolean;
  audienceViewMode?: AudienceViewMode;
  onViewModeChange?: (mode: AudienceViewMode) => void;
  hasAudiences?: boolean;
};

const AudienceStickyHeader = ({
  visible,
  countryState,
  platformState,
  searchState,
  layoutState,
  audienceViewMode,
  onViewModeChange,
  hasAudiences,
}: Props) => {
  
  const filterOptions: FilterOption[] = [
    { label: 'Interest', value: 'Interest' },
    { label: 'Demographic', value: 'Demographic' },
  ];
  
  const handleFilterChange = (values: string[]) => {
    onViewModeChange?.(values as AudienceViewMode);
  };
  
useEffect(() => {
  function updateStickyOffset() {
    const header = document.querySelector('header');
    const headerHeight = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--site-header-height', `${headerHeight}px`);
    }
  updateStickyOffset();
  window.addEventListener('resize', updateStickyOffset);

  const observer = new MutationObserver(updateStickyOffset);
  const header = document.querySelector('header');
  if (header) {
    observer.observe(header, { attributes: true, childList: true, subtree: true });
  }
  return () => {
    window.removeEventListener('resize', updateStickyOffset);
    observer.disconnect();
  };
}, []);


  if (!visible) return null;


  return (
    <Box className="audience-sticky-header">
      <Box className="audience-sticky-inner">
        <Flex direction="column" gap="2">
          {!layoutState?.isCompact && (
            <>
              <Box className="hero-heading">
                <Heading  size="7" mb="2" >Audience List</Heading>
            </Box>
          </>
          )}
          {layoutState?.isCompact ? (
            <Flex direction="column" gap="2">
              <Box className="w-100">
                <TextField.Root
                  size="3"
                  placeholder="Search for an audience…"
                  value={searchState?.searchQuery}
                  onChange={(event) => searchState?.onSearchChange && searchState.onSearchChange(event.target.value)}
                  className="fullwidth"
                >
                  <TextField.Slot side="left">
                    <MagnifyingGlassIcon />
                  </TextField.Slot>
                </TextField.Root>
              </Box>
              <FilterToggleGroup
                options={filterOptions}
                selectedValues={audienceViewMode || []}
                onSelectionChange={handleFilterChange}
                visible={hasAudiences}
                size="2"
                buttonSize='small'
                style="compact"
                justify="center"
              />
            </Flex>
          ) : layoutState?.isMedium ? (
            <Flex direction="column" gap="2">
              <Box style={{ width: '100%' }}>
                <TextField.Root
                  size="3"
                  placeholder="Search for an audience…"
                  value={searchState?.searchQuery}
                  onChange={(event) => searchState?.onSearchChange && searchState?.onSearchChange(event.target.value)}
                >
                  <TextField.Slot side="left">
                    <MagnifyingGlassIcon />
                  </TextField.Slot>
                </TextField.Root>
              </Box>
              <Flex gap="2" wrap="wrap" style={{ width: '100%' }}>
                <FilterToggleGroup
                  options={filterOptions}
                  selectedValues={audienceViewMode || []}
                  onSelectionChange={handleFilterChange}
                  visible={hasAudiences}
                  size="2"
                  buttonSize='small'
                  style="compact"
                  justify="center"
                />
                <Box style={{ flex: '1 1 0%', minWidth: '100px', width: '100%' }}>
                <GenericSelect
                  items={countryState?.countries || []}
                  selectedItem={countryState?.country}
                  onItemChange={countryState?.onCountryChange ?? (() => {})}
                  placeholder="Select country…"
                  modalTrigger={{enabled: true, label: "Country Selector", icon: <ViewHorizontalIcon/>, onTrigger: countryState?.onOpenCountrySelector}}
                  sectionLabel='Countries'
                  emptyOption={{ enabled: true, label: 'No Country', icon: <GlobeIcon/>}}
                  />
                </Box>
                <Box style={{ flex: '1 1 0%', minWidth: '100px', width: '100%' }}>
                  <GenericSelect 
                    items={platformState?.platforms || []} 
                    selectedItem={platformState?.platform} 
                    onItemChange={platformState?.onPlatformChange || (() => {})} 
                    withSearch={false} 
                    renderIcon={() => <DesktopIcon />}
                    otherOptions={{enabled: true, position: 'bottom', label: 'Other', value: 'other'}}
                    emptyOption={{enabled: false}}/>
                </Box>
              </Flex>
            </Flex>
          ) : (
            <>
            <Flex align="center" gap="2" wrap="nowrap">
              <Box style={{ flex: '1 1 50%', minWidth: '360px', maxWidth: '50%' }}>
                <TextField.Root
                  size="3"
                  placeholder="Search for an audience…"
                  value={searchState?.searchQuery}
                  onChange={(event) => searchState?.onSearchChange && searchState?.onSearchChange(event.target.value)}
                >
                  <TextField.Slot side="left">
                    <MagnifyingGlassIcon />
                  </TextField.Slot>
                </TextField.Root>
              </Box>
              <Flex
                gap="5"
                align="center"
                justify="end"
                wrap="nowrap"
                style={{ flexShrink: 0, minWidth: '320px', marginLeft: 'auto', justifyItems: '' }}
              >
                
                <FilterToggleGroup
                  options={filterOptions}
                  selectedValues={audienceViewMode || []}
                  onSelectionChange={handleFilterChange}
                  visible={true}
                  size="3"
                  style="full"
                  gap="4"
                  buttonSize='medium'
                  justify="center"
                  wrap="wrap"
                />
                  <GenericSelect
                  items={countryState?.countries || []}
                  selectedItem={countryState?.country}
                  onItemChange={countryState?.onCountryChange ?? (() => {})}
                  placeholder="Select country…"
                  modalTrigger={{enabled: true, label: "Country Selector", icon: <ViewHorizontalIcon/>, onTrigger: countryState?.onOpenCountrySelector}}
                  sectionLabel='Countries'
                  emptyOption={{ enabled: true, label: 'No Country', icon: <GlobeIcon/>}}
                  />
                <GenericSelect 
                  items={platformState?.platforms || []} 
                  selectedItem={platformState?.platform} 
                  onItemChange={platformState?.onPlatformChange || (() => {})} 
                  withSearch={false}
                  renderIcon={() => <DesktopIcon />}
                  otherOptions={{enabled: true, label: 'Other', value: 'other'}}
                  sectionLabel='DSPs'
                  emptyOption={{enabled: false}}/>
              </Flex>
            </Flex>
            </>
            
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export default AudienceStickyHeader;
